"use client"

import { motion } from "motion/react"
import { ArrowUpRight, Search } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"

import { Badge } from "@/core/ui/badge"
import { Button } from "@/core/ui/button"
import { Input } from "@/core/ui/input"
import { Newsletter } from "@/modules/marketing/writing/components/newsletter"

interface WritingPost {
  _id: string
  title: string
  slug: string
  excerpt: string
  date: string
  readTime: string
  category: string
  featured?: boolean
  coverImage?: string
}

interface WritingClientProps {
  initialPosts: WritingPost[]
}

export function WritingClient({ initialPosts }: WritingClientProps) {
    const allCategories = ["All", ...Array.from(new Set(initialPosts.map(post => post.category).filter(Boolean)))]

    const [selectedCategory, setSelectedCategory] = useState("All")
    const [searchQuery, setSearchQuery] = useState("")

    // Filter posts based on category and search query
    const filteredPosts = initialPosts.filter(post => {
        const matchesCategory = selectedCategory === "All" || post.category === selectedCategory
        const matchesSearch = 
            post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
        return matchesCategory && !!matchesSearch // force boolean
    })

    // Separate featured post (first matching one)
    const featuredPost = filteredPosts.find(p => p.featured) || (filteredPosts.length > 0 ? filteredPosts[0] : null)
    const otherPosts = featuredPost ? filteredPosts.filter(p => p._id !== featuredPost._id) : filteredPosts

    return (
        <section className="min-h-screen bg-black selection:bg-premium-violet/30 selection:text-white">
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-4 overflow-hidden">
                <div className="absolute inset-0 bg-transparent bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))]" />

                <div className="container max-w-6xl mx-auto relative z-10 text-center">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="text-5xl md:text-7xl font-bold bg-clip-text text-transparent bg-linear-to-b from-white via-white/90 to-white/50 mb-6 tracking-tight"
                    >
                        Writings.
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                        className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed"
                    >
                        Exploring the intersection of software architecture, engineering philosophy, and the future of technology.
                    </motion.p>
                </div>
            </section>

            {/* Filter & Search Bar */}
            <section className="sticky top-16 z-40 bg-black/80 backdrop-blur-md border-b border-white/5 py-4 mb-12">
                <div className="container max-w-6xl mx-auto px-4 flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 no-scrollbar">
                        {allCategories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300 border ${selectedCategory === category
                                    ? "bg-white text-black border-white"
                                    : "bg-transparent text-slate-400 border-white/10 hover:border-white/30 hover:text-white"
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                            type="text"
                            placeholder="Search writings..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 h-9 bg-white/5 border-white/10 text-white placeholder:text-slate-600 rounded-full focus:bg-white/10 transition-colors"
                        />
                    </div>
                </div>
            </section>

            {/* Content Grid */}
            <div className="container max-w-6xl mx-auto px-4 pb-24">
                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Featured Post - Takes up 2 cols on Large screens */}
                    {featuredPost && (
                        <Link href={`/writing/${featuredPost.slug}`} className="lg:col-span-3 group relative block">
                            <div className="relative h-[500px] w-full overflow-hidden rounded-3xl border border-white/10">
                                {/* Image */}
                                <div className="absolute inset-0 bg-neutral-900">
                                    {featuredPost.coverImage && (
                                        <Image
                                            src={featuredPost.coverImage}
                                            alt={featuredPost.title}
                                            fill
                                            sizes="(max-width: 1280px) 100vw, 1200px"
                                            className="object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
                                        />
                                    )}
                                    <div className="absolute inset-0 bg-linear-to-t from-black via-black/50 to-transparent" />
                                </div>

                                {/* Content Overlay */}
                                <div className="absolute bottom-0 left-0 p-8 md:p-12 w-full">
                                    {featuredPost.category && (
                                        <Badge className="bg-premium-violet/20 hover:bg-premium-violet/30 text-premium-violet border-none px-3 py-1 mb-4 backdrop-blur-md">
                                            {featuredPost.category}
                                        </Badge>
                                    )}
                                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight group-hover:text-premium-cyan transition-colors duration-300">
                                        {featuredPost.title}
                                    </h2>
                                    <p className="text-slate-300 text-lg max-w-2xl mb-6 line-clamp-2">
                                        {featuredPost.excerpt}
                                    </p>
                                    <div className="flex items-center text-sm text-slate-400 font-medium">
                                        {featuredPost.date && <span>{new Date(featuredPost.date).toLocaleDateString()}</span>}
                                        <span className="mx-2">·</span>
                                        {featuredPost.readTime && <span>{featuredPost.readTime}</span>}
                                        <span className="ml-auto flex items-center text-white group-hover:gap-2 transition-all">
                                            Read Article <ArrowUpRight className="ml-1 w-4 h-4" />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    )}

                    {/* Other Posts Grid */}
                    {otherPosts.map((post) => (
                        <motion.div
                            key={post._id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Link href={`/writing/${post.slug}`} className="group block h-full">
                                <article className="flex flex-col h-full bg-white/5 border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 hover:border-white/20 hover:bg-white/10 hover:-translate-y-1">
                                    <div className="relative w-full aspect-video overflow-hidden">
                                        {post.coverImage && (
                                            <Image
                                                src={post.coverImage}
                                                alt={post.title}
                                                fill
                                                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                                className="object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                        )}
                                        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors" />
                                    </div>

                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-center justify-between mb-3">
                                            {post.category && (
                                                <span className="text-xs font-semibold uppercase tracking-wider text-premium-cyan">
                                                    {post.category}
                                                </span>
                                            )}
                                            {post.readTime && <span className="text-xs text-slate-500">{post.readTime}</span>}
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-3 leading-snug group-hover:text-premium-violet transition-colors">
                                            {post.title}
                                        </h3>
                                        <p className="text-slate-400 text-sm line-clamp-3 mb-4 flex-1">
                                            {post.excerpt}
                                        </p>
                                        <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between text-xs text-slate-500">
                                            {post.date && <span>{new Date(post.date).toLocaleDateString()}</span>}
                                            <span className="group-hover:translate-x-1 transition-transform">Read &rarr;</span>
                                        </div>
                                    </div>
                                </article>
                            </Link>
                        </motion.div>
                    ))}
                </div>

                {/* Empty State */}
                {filteredPosts.length === 0 && (
                    <div className="text-center py-24">
                        <p className="text-slate-500 text-lg">No writings found matching your criteria.</p>
                        <Button
                            variant="link"
                            className="text-premium-cyan mt-2"
                            onClick={() => { setSelectedCategory("All"); setSearchQuery("") }}
                        >
                            Clear filters
                        </Button>
                    </div>
                )}
            </div>

            <Newsletter />
        </section>
    )
}
